import React, { useState, useEffect } from "react";
import { FaRegSave, FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SearchBar from "../../../reusableComps/SearchBar/SearchBar";
import { getProfilePictureUrl } from "../../../../utils/imageUtils";

const EditDelegationContent = ({
  mode = "edit",
  initialName,
  initialLocation,
  initialMembers,
  initialDescription,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const users = useSelector((state) => state.users.items);

  const [name, setName] = useState(initialName);
  const [location, setLocation] = useState(initialLocation);
  const [description, setDescription] = useState(initialDescription || "");
  const [membersSelected, setMembersSelected] = useState(initialMembers || []);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [localUserDelegationOverrides, setLocalUserDelegationOverrides] = useState({});

  useEffect(() => {
    setName(initialName);
    setLocation(initialLocation);
    setDescription(initialDescription || "");
    setMembersSelected(initialMembers || []);
    setLocalUserDelegationOverrides({});
  }, [initialName, initialLocation, initialDescription, initialMembers]);

  useEffect(() => {
    const isNameChanged = name !== initialName;
    const isLocationChanged = location !== initialLocation;
    const isDescriptionChanged = description !== initialDescription;
    const isMembersChanged =
      JSON.stringify(membersSelected) !== JSON.stringify(initialMembers);

    setIsSaveDisabled(
      !(
        isNameChanged ||
        isLocationChanged ||
        isDescriptionChanged ||
        isMembersChanged
      ),
    );
  }, [
    name,
    location,
    description,
    membersSelected,
    initialName,
    initialLocation,
    initialDescription,
    initialMembers,
  ]);

  const availableMembers = users
    .filter((user) => {
      if (!user || membersSelected.some((m) => m.id === user.id)) {
        return false;
      }

      return Object.prototype.hasOwnProperty.call(localUserDelegationOverrides, user.id)
        ? !localUserDelegationOverrides[user.id]
        : !user.delegation_id;
    })
    .map((user) => ({
      id: user.id,
      user_id: user.id,
      name: user.username || user.email,
      role: user.role?.name || t("components.editDelegation.noRole"),
      pic: getProfilePictureUrl(user) || "https://placehold.co/50x50",
    }));

  const filteredAvailableMembers = availableMembers.filter((m) =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name, location, description, members: membersSelected });
  };

  const moveToAvailable = (index) => {
    const member = membersSelected[index];
    setMembersSelected(membersSelected.filter((_, i) => i !== index));
    setLocalUserDelegationOverrides((prev) => ({
      ...prev,
      [member.id]: null,
    }));
  };

  const moveToSelected = (member) => {
    if (
      membersSelected.length < 5 &&
      !membersSelected.some((m) => m.id === member.id)
    ) {
      setMembersSelected([...membersSelected, member]);
      setLocalUserDelegationOverrides((prev) => ({
        ...prev,
        [member.id]: true,
      }));
    }
  };

  return (
    <>
      <div className="modal-header border-bottom-0 pb-0">
        <h1 className="modal-title fs-5 fw-bold txt-main">
          {t(
            mode === "create"
              ? "components.editDelegation.createTitle"
              : "components.editDelegation.title",
          )}
        </h1>
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleSave}
            disabled={isSaveDisabled || !name.trim()}
          >
            <FaRegSave size={14} /> {t("components.editDelegation.buttons.save")}
          </button>
          <button className="btn btn-outline-secondary" onClick={onCancel}>
            <FaTimes />
          </button>
        </div>
      </div>

      <div className="modal-body">
        {/* Form */}
        <div className="mb-3">
          <label
            className="txt-subtle fw-bold text-uppercase mb-1"
            style={{ fontSize: "var(--text-small)" }}
          >
            {t("components.editDelegation.fields.name")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`form-control ${!name.trim() ? "is-invalid" : ""}`}
          />
          {!name.trim() && (
            <div className="invalid-feedback">
              {t("components.editDelegation.validation.nameRequired")}
            </div>
          )}
        </div>

        <div className="mb-3">
          <label
            className="txt-subtle fw-bold text-uppercase mb-1"
            style={{ fontSize: "var(--text-small)" }}
          >
            {t("components.editDelegation.fields.description")}
          </label>
          <textarea
            className="form-control"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        <div className="mb-4">
          <label
            className="txt-subtle fw-bold text-uppercase mb-1"
            style={{ fontSize: "var(--text-small)" }}
          >
            {t("components.editDelegation.fields.location")}
          </label>
          <input
            type="text"
            className="form-control mb-2"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t("components.editDelegation.placeholders.location")}
          />
          {location && (
            <iframe
              title="Mapa"
              className="w-100 rounded"
              height="150"
              src={`https://www.google.com/maps?q=${encodeURIComponent(location)}&output=embed`}
              style={{ border: "var(--border-thin)" }}
            ></iframe>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="m-0 txt-main fw-bold">
            {t("components.editDelegation.sections.selectedMembers")}
          </h5>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowMembersModal(true)}
          >
            + {t("components.editDelegation.buttons.add")}
          </button>
        </div>

        <div
          className="atlar-card overflow-hidden"
          style={{
            border: "var(--border-thin)",
            borderRadius: "var(--border-radius)",
          }}
        >
          {membersSelected.length > 0 ? (
            <table className="table mb-0 align-middle">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="txt-subtle fw-semibold py-3 px-4 border-0"
                    style={{ fontSize: "var(--text-small)" }}
                  >
                    {t("components.editDelegation.table.photo")}
                  </th>
                  <th
                    className="txt-subtle fw-semibold py-3 px-4 border-0"
                    style={{ fontSize: "var(--text-small)" }}
                  >
                    {t("components.editDelegation.table.name")}
                  </th>
                  <th
                    className="txt-subtle fw-semibold py-3 px-4 border-0"
                    style={{ fontSize: "var(--text-small)" }}
                  >
                    {t("components.editDelegation.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {membersSelected.map((member, index) => (
                  <tr
                    key={index}
                    style={{ borderBottom: "var(--border-thin)" }}
                  >
                    <td className="py-3 px-4 border-0">
                      <img
                        src={member.pic}
                        className="rounded-circle"
                        style={{ width: "40px", height: "40px" }}
                        alt=""
                      />
                    </td>
                    <td className="py-3 px-4 border-0 txt-main fw-semibold">
                      {member.name}
                    </td>
                    <td className="py-3 px-4 border-0">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => moveToAvailable(index)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="p-4 text-center txt-subtle">
              {t("components.editDelegation.emptySelected")}
            </p>
          )}
        </div>
        {showMembersModal && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content bg-surface">
                <div className="modal-header border-0">
                  <h5 className="modal-title txt-main">
                    {t("components.editDelegation.modals.addMembersTitle")}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowMembersModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <SearchBar
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t("components.editDelegation.placeholders.search")}
                  />
                  <div
                    className="mt-3"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {filteredAvailableMembers.map((member, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between p-2 border-bottom"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <img
                            src={member.pic}
                            className="rounded-circle"
                            style={{ width: "30px", height: "30px" }}
                            alt=""
                          />
                          <span className="txt-main">{member.name}</span>
                        </div>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => moveToSelected(member)}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EditDelegationContent;
